import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
/* eslint-disable default-case */

/* eslint-disable consistent-return */

import React, { useRef, useCallback, useMemo, useState } from 'react';
import { Portal } from "../Portal";
import { useFocusTrap, useBodyScrollLock, useToggleFocus } from "../../hooks";
import { useHideBody } from "../Dialog/useHideBody";
import styled, { css } from 'styled-components';
import { theme } from "../../theme";
import { Box } from "../Box";
import { SheetContext } from "./SheetContext";
import { easeCurves } from "../../motion/eases";
import { SheetHead } from "./SheetHead";
import { SheetContent } from "./SheetContent";
import { Transition } from 'react-transition-group';
import { noop } from "../../utils/noop";
import { reflow } from "../../utils/reflow";
import { SheetTransitionStatusProvider } from "./useSheetTransitionStatus";
import { IconSheetCollapse } from "@frameio/vapor-icons/build/esm/icons/IconSheetCollapse";
var SHEET_ENTER_DURATION = 300;
var SHEET_EXIT_DURATION = 250;
export function Sheet(_ref) {
  var initialFocusRef = _ref.initialFocusRef,
      onRequestClose = _ref.onRequestClose,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? 'left' : _ref$placement,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 0 : _ref$offset,
      children = _ref.children,
      title = _ref.title,
      _ref$isShowing = _ref.isShowing,
      isShowing = _ref$isShowing === void 0 ? false : _ref$isShowing,
      id = _ref.id,
      _ref$shouldUseOverlay = _ref.shouldUseOverlay,
      shouldUseOverlay = _ref$shouldUseOverlay === void 0 ? true : _ref$shouldUseOverlay,
      style = _ref.style,
      _ref$shouldUsePortal = _ref.shouldUsePortal,
      shouldUsePortal = _ref$shouldUsePortal === void 0 ? true : _ref$shouldUsePortal,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 400 : _ref$size,
      _ref$margin = _ref.margin,
      margin = _ref$margin === void 0 ? 2 : _ref$margin,
      _ref$appear = _ref.appear,
      appear = _ref$appear === void 0 ? false : _ref$appear,
      _ref$onEnter = _ref.onEnter,
      onEnter = _ref$onEnter === void 0 ? noop : _ref$onEnter,
      _ref$onEntering = _ref.onEntering,
      onEntering = _ref$onEntering === void 0 ? noop : _ref$onEntering,
      _ref$onEntered = _ref.onEntered,
      onEntered = _ref$onEntered === void 0 ? noop : _ref$onEntered,
      _ref$onExit = _ref.onExit,
      onExit = _ref$onExit === void 0 ? noop : _ref$onExit,
      _ref$onExiting = _ref.onExiting,
      onExiting = _ref$onExiting === void 0 ? noop : _ref$onExiting,
      _ref$onExited = _ref.onExited,
      onExited = _ref$onExited === void 0 ? noop : _ref$onExited,
      _ref$radius = _ref.radius,
      radius = _ref$radius === void 0 ? "".concat(theme.radii[8]) : _ref$radius,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? 'grey15' : _ref$backgroundColor,
      _ref$collapseButtonLa = _ref.collapseButtonLabel,
      collapseButtonLabel = _ref$collapseButtonLa === void 0 ? 'Collapse' : _ref$collapseButtonLa,
      other = _objectWithoutProperties(_ref, ["initialFocusRef", "onRequestClose", "placement", "offset", "children", "title", "isShowing", "id", "shouldUseOverlay", "style", "shouldUsePortal", "size", "margin", "appear", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "radius", "backgroundColor", "collapseButtonLabel"]);

  var ref = useRef(null);
  var containerRef = useRef(null);

  var _useState = useState(isShowing && !appear),
      _useState2 = _slicedToArray(_useState, 2),
      hasEntered = _useState2[0],
      setHasEntered = _useState2[1]; // trap focus inside our sheet when the overlay is enabled


  var handleTab = useFocusTrap(ref); // manage focus

  useToggleFocus(initialFocusRef !== null && initialFocusRef !== void 0 ? initialFocusRef : ref, isShowing); // lock body scrolling when using "Portal" mode

  useBodyScrollLock({
    enabled: isShowing && shouldUsePortal
  }); // set aria-hidden to all elements besides this one when visible

  useHideBody(containerRef, isShowing && shouldUseOverlay); // close sheet when escape is pressed

  var onKeyDown = useCallback(function (e) {
    // Trap focus when the overlay is enabled
    if (shouldUseOverlay) {
      handleTab(e);
    }

    if (e.key === 'Escape') {
      // if we have a sheet within a sheet (plz no), only close
      // the first one.
      e.stopPropagation();
      onRequestClose();
    }
  }, [shouldUseOverlay, handleTab, onRequestClose]);
  var sheetContext = useMemo(function () {
    return {
      onRequestClose: onRequestClose,
      isShowing: isShowing,
      id: id,
      size: size
    };
  }, [id, isShowing, onRequestClose, size]); // a forced reflow is necessary to get our
  // mount animation to render.

  var handleEnter = useCallback(function (el, isAppearing) {
    reflow(el);

    if (onEnter) {
      onEnter(el, isAppearing);
    }
  }, [onEnter]);
  var handleEntered = useCallback(function (el, isAppearing) {
    if (onEntered) {
      onEntered(el, isAppearing);
    }

    setHasEntered(true);
  }, [onEntered]);
  var handleExited = useCallback(function (el) {
    if (onExited) {
      onExited(el);
    }

    setHasEntered(false);
  }, [onExited]);
  return /*#__PURE__*/React.createElement(Portal, {
    disabled: !shouldUsePortal
  }, /*#__PURE__*/React.createElement(Transition, {
    appear: appear,
    in: isShowing,
    mountOnEnter: true,
    unmountOnExit: true,
    onEnter: handleEnter,
    onEntering: onEntering,
    onEntered: handleEntered,
    onExit: onExit,
    onExiting: onExiting,
    onExited: handleExited,
    timeout: {
      enter: SHEET_ENTER_DURATION,
      exit: SHEET_EXIT_DURATION
    }
  }, function (state) {
    return /*#__PURE__*/React.createElement(SheetTransitionStatusProvider, {
      mountTransitionComplete: hasEntered
    }, /*#__PURE__*/React.createElement(OffsetContainer, {
      "data-testid": "sheet-container",
      ref: containerRef,
      $size: size,
      $offset: offset,
      $placement: placement,
      $usePortal: shouldUsePortal,
      $margin: margin,
      $radius: radius,
      $backgroundColor: backgroundColor
    }, shouldUseOverlay && /*#__PURE__*/React.createElement(Overlay, {
      "data-testid": "overlay",
      "aria-hidden": true,
      onClick: onRequestClose
    }), /*#__PURE__*/React.createElement(StyledSheet, _extends({
      $usePortal: shouldUsePortal,
      $backgroundColor: backgroundColor,
      "data-state": state,
      $offset: offset,
      $placement: placement,
      $size: size,
      $margin: margin,
      $radius: radius,
      onKeyDown: onKeyDown,
      "aria-labelledby": !other['aria-label'] && id,
      ref: ref,
      tabIndex: -1,
      style: style,
      onClick: function onClick(e) {
        return e.stopPropagation();
      }
    }, other), /*#__PURE__*/React.createElement(SheetContext.Provider, {
      value: sheetContext
    }, /*#__PURE__*/React.createElement(SheetContentContainer, null, title && /*#__PURE__*/React.createElement(SheetHead, {
      title: title,
      id: id,
      closeIcon: /*#__PURE__*/React.createElement(CollapseIcon, {
        rotate: iconRotation(placement)
      }),
      onRequestClose: onRequestClose,
      collapseButtonLabel: collapseButtonLabel
    }), /*#__PURE__*/React.createElement(SheetContent, {
      size: size,
      id: id
    }, typeof children === 'function' ? children({
      mountTransitionComplete: hasEntered
    }) : children))))));
  }));
}

var CollapseIcon = function CollapseIcon(_ref2) {
  var rotate = _ref2.rotate;
  return /*#__PURE__*/React.createElement(Box, {
    width: "24",
    height: "24",
    style: {
      transform: "rotate(".concat(rotate, ")")
    }
  }, /*#__PURE__*/React.createElement(IconSheetCollapse, null));
};

var SheetContentContainer = styled.div.withConfig({
  displayName: "Sheet__SheetContentContainer",
  componentId: "vapor__sc-3kb85d-0"
})(["height:100%;width:100%;display:flex;flex-direction:column;border-radius:", ";overflow:hidden;"], theme.radii[8]);
var Overlay = styled.div.withConfig({
  displayName: "Sheet__Overlay",
  componentId: "vapor__sc-3kb85d-1"
})(["touch-action:none;position:absolute;pointer-events:auto;top:0;left:0;right:0;bottom:0;opacity:0;z-index:0;background-color:transparent;"]);
var OffsetContainer = styled.div.withConfig({
  displayName: "Sheet__OffsetContainer",
  componentId: "vapor__sc-3kb85d-2"
})(["overflow:hidden;position:", ";pointer-events:none;z-index:", ";border-radius:", ";", ";"], function (p) {
  return p.$usePortal ? 'fixed' : 'absolute';
}, theme.zIndices.sheet, function (p) {
  return p.$radius;
}, getOverflowContainerPlacement);
var StyledSheet = styled.div.withConfig({
  displayName: "Sheet__StyledSheet",
  componentId: "vapor__sc-3kb85d-3"
})(["opacity:1;pointer-events:auto;outline:none;background-color:", ";border-radius:", ";position:relative;z-index:1;display:inline-flex;transition-property:transform,width;backface-visibility:hidden;will-change:transform;&:before{border-radius:", ";position:absolute;top:0;left:0;bottom:0;right:0;content:'';box-shadow:0 0 30px rgba(0,0,0,1);}&[data-state='entering']:before,&[data-state='entered']:before{opacity:1;transition-duration:", "ms;transition-timing-function:", ";}&[data-state='exiting']:before,&[data-state='exited']:before{opacity:0;transition-duration:", "ms;transition-timing-function:", ";}", ";"], function (p) {
  return theme.colors.palette[p.$backgroundColor];
}, function (p) {
  return p.$radius;
}, function (p) {
  return p.$radius;
}, SHEET_ENTER_DURATION, easeCurves.primary, SHEET_EXIT_DURATION, easeCurves.primary, getPlacement);

function getOverflowContainerPlacement(_ref3) {
  var $placement = _ref3.$placement,
      $offset = _ref3.$offset,
      $margin = _ref3.$margin;

  switch ($placement) {
    case 'left':
      return css(["left:", "px;top:", "px;bottom:", "px;right:0;display:flex;justify-content:flex-start;"], $offset + $margin, $margin, $margin);

    case 'right':
      return css(["right:", "px;top:", "px;bottom:", "px;left:0;display:flex;justify-content:flex-end;"], $offset + $margin, $margin, $margin);

    case 'top':
      return css(["top:", "px;left:", "px;right:", "px;bottom:0;display:flex;flex-direction:column;justify-content:flex-start;"], $offset + $margin, $margin, $margin);

    case 'bottom':
      return css(["bottom:", "px;left:", "px;right:", "px;top:0;display:flex;flex-direction:column;justify-content:flex-end;"], $offset + $margin, $margin, $margin);
  }
}

function getPlacement(_ref4) {
  var $placement = _ref4.$placement,
      $size = _ref4.$size;

  switch ($placement) {
    case 'left':
      return css(["height:100%;width:", "px;&[data-state='entering'],&[data-state='entered']{transform:translateX(0);transition-duration:", "ms;transition-timing-function:", ";}&[data-state='exiting'],&[data-state='exited']{transition-duration:", "ms;transition-timing-function:", ";transform:translateX(-", "px);}"], $size, SHEET_ENTER_DURATION, easeCurves.primary, SHEET_EXIT_DURATION, easeCurves.primary, $size);

    case 'right':
      return css(["height:100%;width:", "px;&[data-state='entering'],&[data-state='entered']{transform:translateX(0px);transition-duration:", "ms;transition-timing-function:", ";}&[data-state='exiting'],&[data-state='exited']{transition-duration:", "ms;transition-timing-function:", ";transform:translateX(", "px);}"], $size, SHEET_ENTER_DURATION, easeCurves.primary, SHEET_EXIT_DURATION, easeCurves.primary, $size);

    case 'top':
      return css(["width:100%;height:", "px;&[data-state='entering'],&[data-state='entered']{transform:translateY(0);transition-duration:", "ms;transition-timing-function:", ";}&[data-state='exiting'],&[data-state='exited']{transition-duration:", "ms;transition-timing-function:", ";transform:translateY(-", "px);}"], $size, SHEET_ENTER_DURATION, easeCurves.primary, SHEET_EXIT_DURATION, easeCurves.primary, $size);

    case 'bottom':
      return css(["width:100%;height:", "px;&[data-state='entering'],&[data-state='entered']{transform:translateY(0px);transition-duration:", "ms;transition-timing-function:", ";}&[data-state='exiting'],&[data-state='exited']{transition-duration:", "ms;transition-timing-function:", ";transform:translateY(", "px);}"], $size, SHEET_ENTER_DURATION, easeCurves.primary, SHEET_EXIT_DURATION, easeCurves.primary, $size);

    default:
      throw new Error('Sheet must have a placement argument');
  }
}

function iconRotation(placement) {
  switch (placement) {
    case 'left':
      return '0deg';

    case 'right':
      return '180deg';

    case 'top':
      return '90deg';

    case 'bottom':
      return '270deg';
  }
}